import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";

const airports = {
  heathrow: {
    key: "heathrow",
    name: "Heathrow",
    code: "LHR",
    path: "/heathrow",
    accent: "#1d4ed8",
    summary: "Live road, rail, terminal and flight-aware planning for Heathrow journeys.",
    status: {
      road: "Good",
      rail: "Good",
      flights: "Moderate",
      terminals: "Good",
      queues: "Good",
      weather: "11C • Light cloud",
      overall: "Good",
    },
    preview: {
      arrivals: "469",
      departures: "236",
      terminal: "T2 Good • T3 Busy • T4 Good • T5 Good",
      weather: "11C • Light cloud",
      flights: [
        "BA123 New York (JFK) • On time",
        "VS43 Atlanta (ATL) • On time",
        "LH247 Frankfurt (FRA) • Delayed 20m",
      ],
      queues: [
        "Security T2 • 0-5 min",
        "Security T3 • 10-15 min",
        "Border Control T2 • 5-10 min",
      ],
      road: [
        "M25 (J15-J16) • Good",
        "M4 (J4-J4b) • Good",
        "A4 Bath Road • Moderate",
      ],
      rail: [
        "Heathrow Express • On time",
        "Elizabeth line • Good service",
        "South Western Railway • Good service",
      ],
    },
  },
  ema: {
    key: "ema",
    name: "East Midlands Airport",
    code: "EMA",
    path: "/east-midlands-airport",
    accent: "#0f766e",
    summary: "Deterministic planning for EMA with road corridor, rail gateway and flight checks.",
    status: {
      road: "Good",
      rail: "Good",
      flights: "On time",
      terminals: "Good",
      queues: "Good",
      weather: "10C • Partly cloudy",
      overall: "Good",
    },
    preview: {
      arrivals: "111",
      departures: "93",
      terminal: "Main Terminal • Good",
      weather: "10C • Partly cloudy",
      flights: [
        "FR669 Dublin (DUB) • On time",
        "LS155 Palma (PMI) • On time",
        "FR247 Malaga (AGP) • Delayed 15m",
      ],
      queues: [
        "Security • 0-5 min",
        "Fast Track • 0-5 min",
        "Border Control • 5-10 min",
      ],
      road: [
        "M1 (J23-J24) • Good",
        "A46 (North) • Good",
        "A453 • Good",
      ],
      rail: [
        "EMR Services • On time",
        "CrossCountry • On time",
        "East Midlands Trains • On time",
      ],
    },
  },
};

const airportKeyByCode = {
  LHR: "heathrow",
  EMA: "ema",
};

const airportPlannerOptions = [
  { key: "heathrow", label: "Heathrow (LHR)" },
  { key: "ema", label: "East Midlands Airport (EMA)" },
];

function statusRank(value) {
  const key = String(value || "").toLowerCase();
  if (key.includes("severe") || key.includes("red") || key.includes("poor")) return 5;
  if (key.includes("busy") || key.includes("moderate") || key.includes("amber")) return 4;
  if (key.includes("watch") || key.includes("light")) return 3;
  if (key.includes("good") || key.includes("green") || key.includes("on time")) return 2;
  return 1;
}

const deriveTerminalStatus = (baseAirport, preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : [];
  const structuredLevel = structured.length
    ? structured
        .slice()
        .sort((a, b) => (statusRank(b?.label || b?.level) || 0) - (statusRank(a?.label || a?.level) || 0))[0]
    : null;
  if (structuredLevel) return structuredLevel.label || structuredLevel.level || "Checking";
  const terminalText = String(preview.terminal || baseAirport?.preview?.terminal || "");
  if (/severe|red|poor/i.test(terminalText)) return "Severe";
  if (/busy|moderate|amber|pressure/i.test(terminalText)) return "Busy";
  if (/checking|updating/i.test(terminalText)) return "Checking";
  return "Good";
};

const deriveQueueStatus = (baseAirport, preview = {}) => {
  const queues = Array.isArray(preview.terminal_queues) ? preview.terminal_queues : [];
  const levels = queues.map((row) => String(row?.level || row?.label || "").toLowerCase());
  if (levels.some((level) => level.includes("red") || level.includes("severe"))) return "Busy";
  if (levels.some((level) => level.includes("amber") || level.includes("moderate") || level.includes("busy"))) return "Busy";
  if (levels.some((level) => level.includes("unknown") || level.includes("checking"))) return "Checking";
  return "Good";
};

const normalizeLiveAirport = (baseAirport, liveAirport) => {
  if (!liveAirport || !baseAirport) return baseAirport;
  const hero = liveAirport.hero_status || {};
  const preview = liveAirport.preview || {};
  const normalizedPreview = {
    arrivals: preview.arrivals || baseAirport.preview.arrivals,
    departures: preview.departures || baseAirport.preview.departures,
    terminal: preview.terminal || baseAirport.preview.terminal,
    weather: preview.weather || baseAirport.preview.weather,
    flights: Array.isArray(preview.flights) && preview.flights.length ? preview.flights : baseAirport.preview.flights,
    queues: Array.isArray(preview.queues) && preview.queues.length ? preview.queues : baseAirport.preview.queues,
    terminal_queues: Array.isArray(preview.terminal_queues) ? preview.terminal_queues : baseAirport.preview.terminal_queues || [],
    terminal_pressure: Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : baseAirport.preview.terminal_pressure || [],
    road: Array.isArray(preview.road) && preview.road.length ? preview.road : baseAirport.preview.road,
    rail: Array.isArray(preview.rail) && preview.rail.length ? preview.rail : baseAirport.preview.rail,
  };
  return {
    ...baseAirport,
    liveReason: liveAirport.overall?.reason || baseAirport.liveReason,
    liveSummary: liveAirport.summary?.headline || baseAirport.liveSummary,
    nextStep: liveAirport.summary?.next_step || baseAirport.nextStep,
    nextStepDetail: liveAirport.summary?.detail || baseAirport.nextStepDetail,
    status: {
      road: hero.road?.label || baseAirport.status.road,
      rail: hero.rail?.label || baseAirport.status.rail,
      flights: hero.flights?.label || baseAirport.status.flights,
      terminals: hero.terminals?.label || hero.terminal?.label || deriveTerminalStatus(baseAirport, normalizedPreview),
      queues: hero.queues?.label || deriveQueueStatus(baseAirport, normalizedPreview),
      weather: hero.weather?.label || baseAirport.status.weather,
      overall: liveAirport.overall?.label || baseAirport.status.overall,
    },
    statusReasons: {
      road: hero.road?.reason || hero.road?.headline || "",
      rail: hero.rail?.reason || hero.rail?.headline || "",
      flights: hero.flights?.reason || hero.flights?.headline || "",
      terminals: hero.terminals?.reason || hero.terminal?.reason || normalizedPreview.terminal || "",
      queues: hero.queues?.reason || normalizedPreview.queues?.[0] || "",
      weather: hero.weather?.reason || hero.weather?.headline || "",
      overall: liveAirport.overall?.reason || "",
    },
    statusHeadlines: {
      road: shortStatusHeadline("road", hero.road?.headline || hero.road?.reason || ""),
      rail: shortStatusHeadline("rail", hero.rail?.headline || hero.rail?.reason || ""),
      flights: shortStatusHeadline("flights", hero.flights?.headline || hero.flights?.reason || ""),
      terminals: shortStatusHeadline("terminals", hero.terminals?.headline || hero.terminal?.headline || normalizedPreview.terminal || ""),
      queues: shortStatusHeadline("queues", hero.queues?.headline || normalizedPreview.queues?.[0] || ""),
      weather: shortStatusHeadline("weather", hero.weather?.headline || hero.weather?.reason || ""),
    },
    preview: normalizedPreview,
  };
};

const shortStatusHeadline = (key, value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (key === "road") {
    const segmentMatch = text.match(/most affected segment\s+([^.;]+)/i);
    if (segmentMatch?.[1]) return `${segmentMatch[1].trim()} delays`;
    const deltaMatch = text.match(/:\s*([^.;]+)/);
    if (deltaMatch?.[1]) return deltaMatch[1].trim();
  }
  if (key === "rail") {
    return text.replace(/^([^:]+):\s*/, "").replace(/\s*\|\s*/g, " / ");
  }
  if (key === "flights") {
    if (text.includes("•")) return text;
    const delayedMatch = text.match(/(\d+\s+delayed,\s+\d+\s+cancelled)/i);
    if (delayedMatch?.[1]) return delayedMatch[1];
    return text.replace(/\s+in\s+the\s+.+$/i, "");
  }
  if (key === "weather") {
    return text.split(";")[0].trim();
  }
  if (key === "terminals" || key === "queues") {
    return text.length > 42 ? `${text.slice(0, 39).trim()}...` : text;
  }
  return text.length > 42 ? `${text.slice(0, 39).trim()}...` : text;
};

const statusLevelWord = (value) => String(value || "checking").toLowerCase();

const airportPhrase = (airport) => (airport?.code === "LHR" ? "Heathrow" : airport?.code === "EMA" ? "East Midlands Airport" : "the airport");

const readableRoadText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const count = text.match(/(\d+)\s+monitored\s+road\s+corridors/i)?.[1];
  const worst = text.match(/Worst corridor\s+([^.;]+)/i)?.[1];
  const delay = text.match(/is\s+([+-]?\d+\s*m)\s+vs\s+usual/i)?.[1];
  const level = statusLevelWord(airport?.status?.road);

  if (level === "good") {
    if (worst && delay && !delay.startsWith("-") && !delay.startsWith("+0")) {
      return `Roads and motorways to ${airportName} look clear overall. Slowest route: ${worst.trim()} (${delay.replace("+", "")} slower than usual).`;
    }
    return `Roads and motorways to ${airportName} look clear across ${count || "the"} monitored routes.`;
  }
  if (level === "moderate") {
    return worst
      ? `Some delays on roads and motorways to ${airportName}. Slowest route: ${worst.trim()}${delay ? ` (${delay.replace("+", "")} slower than usual)` : ""}.`
      : `Some delays on roads and motorways to ${airportName}. Allow extra time.`;
  }
  if (level === "poor" || level === "severe") {
    return worst
      ? `Heavy delays on roads and motorways to ${airportName}. Main issue: ${worst.trim()}.`
      : `Heavy delays on roads and motorways to ${airportName}. Check before you leave.`;
  }
  return `Road and motorway checks for ${airportName} are updating.`;
};

const readableRailText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const count = text.match(/(\d+)\s+rail\s+board\/service\s+checks/i)?.[1];
  const level = statusLevelWord(airport?.status?.rail);

  if (level === "good") return `Train services we track for ${airportName} look normal${count ? ` across ${count} checks` : ""}.`;
  if (level === "moderate") return `Some train services for ${airportName} may be delayed or disrupted. Check your route before travelling.`;
  if (level === "poor" || level === "severe") return `Major train disruption may affect journeys to ${airportName}. Check alternatives before travelling.`;
  return `Train service checks for ${airportName} are updating.`;
};

const readableFlightText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const airportName = airportPhrase(airport);
  const cancelled = text.match(/(\d+)\s+cancelled/i)?.[1];
  const delayed30 = text.match(/(\d+)\s+delayed\s+30m\+/i)?.[1];
  const delayed60 = text.match(/(\d+)\s+delayed\s+60m\+/i)?.[1];
  const parts = [];
  if (cancelled) parts.push(`${cancelled} cancelled`);
  if (delayed30) parts.push(`${delayed30} delayed 30+ min`);
  if (delayed60) parts.push(`${delayed60} delayed 60+ min`);
  if (parts.length) return `Flight disruption at ${airportName}: ${parts.join(", ")}.`;
  if (text) return text.replace(/\s+in\s+the\s+.+$/i, "");
  return `Flight checks for ${airportName} are updating.`;
};

const readableWeatherText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text) return `Weather near ${airportPhrase(airport)}: ${text.split(";")[0].trim()}.`;
  return `Weather checks for ${airportPhrase(airport)} are updating.`;
};

const readableTerminalText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text && !/checking|updating/i.test(text)) return text;
  return airport?.code === "LHR" ? "Terminal pressure monitored across T2, T3, T4 and T5." : "Terminal operations look normal.";
};

const readableQueueText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  if (text && !/checking|updating/i.test(text)) return text;
  return airport?.code === "LHR" ? "Security and border queues monitored by terminal." : "Terminal queues look quiet.";
};

const readableOverallText = (airport, detail = "") => {
  const text = String(detail || "").trim();
  const level = statusLevelWord(airport?.status?.overall);
  const airportName = airportPhrase(airport);
  if (/flights currently set/i.test(text)) return `Flight delays are the main thing affecting ${airportName} right now.`;
  if (/road/i.test(text)) return `Road and motorway conditions are the main thing affecting ${airportName} right now.`;
  if (/rail/i.test(text)) return `Train disruption is the main thing affecting ${airportName} right now.`;
  if (/weather/i.test(text)) return `Weather is the main thing affecting ${airportName} right now.`;
  if (level === "good") return `${airportName} looks good overall.`;
  if (level === "moderate") return `${airportName} has some disruption. Check the details before travelling.`;
  if (level === "poor" || level === "severe") return `${airportName} has significant disruption. Check your journey before travelling.`;
  return `Live status for ${airportName} is updating.`;
};

const readableStatusText = (airport, key, detail = "") => {
  if (key === "road") return readableRoadText(airport, detail);
  if (key === "rail") return readableRailText(airport, detail);
  if (key === "flights") return readableFlightText(airport, detail);
  if (key === "terminals") return readableTerminalText(airport, detail);
  if (key === "queues") return readableQueueText(airport, detail);
  if (key === "weather") return readableWeatherText(airport, detail);
  if (key === "overall") return readableOverallText(airport, detail);
  return String(detail || "").trim();
};

const readableStatusHeadline = (airport, key) => {
  const detail = airport?.statusReasons?.[key] || airport?.statusHeadlines?.[key] || "";
  return readableStatusText(airport, key, detail);
};

const checkCards = [
  {
    icon: "✈️",
    title: "Flights & terminals",
    body: "Live arrivals and departures, terminal changes, gate updates and delay context.",
  },
  {
    icon: "🚆",
    title: "Rail & station boards",
    body: "Real-time train times, platforms, service disruption and transfer connections.",
  },
  {
    icon: "🚗",
    title: "Motorways & road corridors",
    body: "Live traffic, incidents, corridor summaries and timing pressure on key approaches.",
  },
  {
    icon: "🅿️",
    title: "Fares & parking",
    body: "Parking references, transfer assumptions, drop-off context and mode comparison inputs.",
  },
  {
    icon: "🌤️",
    title: "Weather & disruption signals",
    body: "Airport and home weather, visibility, wind and travel-impact conditions.",
  },
  {
    icon: "👥",
    title: "Queues & terminal flow",
    body: "Security, border control and terminal pressure summaries where available.",
  },
];

const helpSteps = [
  {
    title: "Enter journey details",
    body: "Choose your airport, add your postcode and optionally include a flight number.",
  },
  {
    title: "We check live conditions",
    body: "Road, rail, weather and flight sources are combined against the airport journey model.",
  },
  {
    title: "Open the planner",
    body: "You move into the airport-specific planner with the right flow, checks and live logic.",
  },
];

const coverageLive = ["Heathrow (LHR)", "East Midlands Airport (EMA)"];
const coverageSoon = ["Gatwick (LGW)", "Stansted (STN)", "Luton (LTN)", "London City (LCY)", "Manchester (MAN)", "Birmingham (BHX)"];
const heroChips = ["Live & reliable", "Multi-source data", "Deterministic checks", "Save time & stress", "Proactive alerts"];
const heroMetrics = [
  { value: "10K+", label: "Checks per day", icon: "⌁" },
  { value: "Thousands", label: "Journeys planned", icon: "◎" },
  { value: "Real-time", label: "Updates every 1-2 min", icon: "◔" },
  { value: "Trusted", label: "Leading data sources", icon: "◈" },
  { value: "Accurate", label: "Deterministic engine", icon: "◉" },
];
const sourceList = [
  { name: "National Rail", mark: "✺" },
  { name: "National Highways", mark: "▲" },
  { name: "Met Office", mark: "≈" },
  { name: "Heathrow", mark: "◌" },
  { name: "Airport Systems", mark: "□" },
  { name: "& more", mark: "+" },
];
const sourceIcons = {
  road: "🚘",
  rail: "🚆",
  flights: "✈️",
  terminals: "🏢",
  queues: "👥",
  weather: "☁️",
  overall: "⏱️",
};

const statusTone = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("good") || text.includes("on time") || text === "green") return { bg: "#dcfce7", fg: "#166534" };
  if (text.includes("watch") || text === "light_green") return { bg: "#e0f2fe", fg: "#075985" };
  if (text.includes("moderate") || text.includes("busy") || text === "amber") return { bg: "#fef3c7", fg: "#92400e" };
  if (text.includes("severe") || text === "red" || text === "critical_red") return { bg: "#fee2e2", fg: "#991b1b" };
  return { bg: "#e5e7eb", fg: "#374151" };
};

const statusSurface = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("severe") || text === "red" || text === "critical_red") {
    return { bg: "linear-gradient(180deg, #fff8f8 0%, #fff1f2 100%)", border: "#fecaca" };
  }
  if (text.includes("moderate") || text.includes("busy") || text === "amber") {
    return { bg: "linear-gradient(180deg, #fffdf7 0%, #fff7ed 100%)", border: "#fed7aa" };
  }
  if (text.includes("watch") || text === "light_green") {
    return { bg: "linear-gradient(180deg, #f8fcff 0%, #f0f9ff 100%)", border: "#bae6fd" };
  }
  if (text.includes("good") || text.includes("on time") || text === "green") {
    return { bg: "linear-gradient(180deg, #fbfffd 0%, #f0fdf4 100%)", border: "#bbf7d0" };
  }
  return { bg: "#ffffff", border: "#dbe7ff" };
};

const previewCardShell = (isCompact) => ({
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(190, 208, 236, 0.95)",
  borderRadius: isCompact ? 16 : 18,
  padding: isCompact ? 12 : 16,
  color: "#08204a",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
});

const previewSectionShell = (status, isCompact) => {
  const surface = statusSurface(status);
  return {
    border: `1px solid ${surface.border}`,
    borderRadius: isCompact ? 14 : 16,
    padding: isCompact ? 11 : 13,
    background: surface.bg,
    alignSelf: "start",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
  };
};

function StatusPill({ status, compact = false }) {
  const tone = statusTone(status);
  return (
    <span
      style={{
        flex: "0 0 auto",
        minWidth: compact ? 70 : 82,
        textAlign: "center",
        padding: compact ? "3px 7px" : "4px 8px",
        borderRadius: 999,
        background: tone.bg,
        color: tone.fg,
        fontSize: compact ? 9 : 10,
        fontWeight: 900,
        textTransform: "uppercase",
        boxSizing: "border-box",
      }}
    >
      {status}
    </span>
  );
}

const useCompactMobile = (maxWidth = 760) => {
  const getMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  };
  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIsCompact(!!e.matches);
    setIsCompact(mq.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [maxWidth]);

  return isCompact;
};

function HeroPlannerCard({ airportKey, airportList, form, onAirportChange, onChange, onSubmit }) {
  const isCompact = useCompactMobile();
  const selectedAirport = airportList.find((airport) => airport.key === airportKey) || airportList[0];
  const compactInputStyle = {
    width: "100%",
    minHeight: isCompact ? 58 : 60,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid rgba(147, 197, 253, 0.28)",
    background: "rgba(15, 35, 70, 0.74)",
    color: "#eff6ff",
    padding: "18px 16px 10px",
    fontSize: 15,
    fontWeight: 750,
    outline: "none",
  };
  const labelStyle = {
    position: "absolute",
    top: 8,
    left: 16,
    color: "#bdd2f5",
    fontSize: 11,
    fontWeight: 750,
    pointerEvents: "none",
  };

  return (
    <section
      style={{
        marginTop: isCompact ? 16 : 28,
        background:
          "radial-gradient(circle at 80% 0%, rgba(37,99,235,0.22), transparent 32%), linear-gradient(145deg, #08245f 0%, #061b46 52%, #082a63 100%)",
        border: "1px solid rgba(191, 219, 254, 0.34)",
        borderRadius: isCompact ? 18 : 18,
        padding: isCompact ? 16 : 24,
        color: "#eff6ff",
        boxShadow: "0 22px 48px rgba(29, 78, 216, 0.24)",
      }}
    >
      <div style={{ color: "#1d7cff", fontSize: 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.03em" }}>
        Plan your journey
      </div>
      <h2 style={{ margin: "10px 0 4px", fontSize: isCompact ? 22 : 22, lineHeight: 1.15 }}>
        Where are you travelling from?
      </h2>
      <p style={{ margin: 0, color: "#bdd2f5", fontSize: 13 }}>
        Add a postcode and optional flight number. The planner will handle timing, routes and transport choices on the next step.
      </p>

      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(240px, 1.15fr) 34px minmax(220px, 1fr) minmax(190px, 0.8fr) minmax(180px, 0.75fr)",
          gap: isCompact ? 10 : 14,
          alignItems: "end",
        }}
      >
        <label style={{ position: "relative", display: "block" }}>
          <span style={labelStyle}>From</span>
          <input
            value={form.postcode}
            onChange={(event) => onChange("postcode", event.target.value)}
            placeholder="e.g. Oxford, OX1 1AA"
            style={compactInputStyle}
          />
        </label>
        {!isCompact && <div style={{ textAlign: "center", color: "#bdd2f5", fontSize: 22, paddingBottom: 17 }}>⇄</div>}
        <label style={{ position: "relative", display: "block" }}>
          <span style={labelStyle}>To airport</span>
          <select
            value={airportKey}
            onChange={(event) => onAirportChange(event.target.value)}
            style={{ ...compactInputStyle, appearance: "none", cursor: "pointer" }}
          >
            {airportPlannerOptions.map((option) => (
              <option key={option.key} value={option.key} style={{ color: "#071948" }}>
                {option.label}
              </option>
            ))}
          </select>
          <span style={{ position: "absolute", right: 14, top: 22, color: "#bdd2f5", pointerEvents: "none" }}>⌄</span>
        </label>
        <label style={{ position: "relative", display: "block" }}>
          <span style={labelStyle}>Flight number</span>
          <input
            value={form.flight}
            onChange={(event) => onChange("flight", event.target.value.toUpperCase())}
            placeholder={selectedAirport?.key === "heathrow" ? "e.g. BA123" : "e.g. FR669"}
            style={compactInputStyle}
          />
        </label>
        {!isCompact && (
          <button
            type="button"
            onClick={() => onSubmit(airportKey)}
            style={{
              minHeight: 60,
              border: "none",
              borderRadius: 10,
              background: "linear-gradient(135deg, #1264ff 0%, #064de7 100%)",
              color: "#ffffff",
              fontSize: 15,
              fontWeight: 900,
              cursor: "pointer",
              boxShadow: "0 18px 34px rgba(37, 99, 235, 0.34)",
            }}
          >
            Plan my journey →
          </button>
        )}
      </div>

      {isCompact && (
        <button
          type="button"
          onClick={() => onSubmit(airportKey)}
          style={{
            marginTop: 14,
            width: "100%",
            minHeight: 60,
            border: "none",
            borderRadius: 10,
            background: "linear-gradient(135deg, #1264ff 0%, #064de7 100%)",
            color: "#ffffff",
            fontSize: 16,
            fontWeight: 900,
            cursor: "pointer",
            boxShadow: "0 18px 34px rgba(37, 99, 235, 0.34)",
          }}
        >
          Plan my journey →
        </button>
      )}

      <div style={{ marginTop: 12, color: "#bdd2f5", fontSize: 12, fontWeight: 750 }}>
        Selected airport: {selectedAirport?.name} ({selectedAirport?.code}) • transport choices stay inside the planner
      </div>
    </section>
  );
}

function PlannerEntryCard({ airport, form, onChange, onSubmit }) {
  const isCompact = useCompactMobile();
  const compactInputStyle = isCompact
    ? { ...inputStyle, minHeight: 40, padding: "8px 10px", fontSize: 13, borderRadius: 10 }
    : inputStyle;
  const compactButtonStyle = isCompact
    ? { ...buttonStyle, minHeight: 42, fontSize: 13, borderRadius: 10 }
    : buttonStyle;

  return (
    <section
      style={{
        background: "#ffffff",
        border: "1px solid #dbe7ff",
        borderRadius: 14,
        padding: 12,
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: `${airport.accent}14`,
              color: airport.accent,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
            }}
          >
            {airport.name} Live
          </div>
          <h3 style={{ margin: "7px 0 3px", fontSize: 19, lineHeight: 1.1, color: "#0f2f6d" }}>
            Open the {airport.code} planner
          </h3>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.4, color: "#38507b", maxWidth: 560 }}>
            {airport.summary}
          </p>
        </div>
        <div
          style={{
            minWidth: 78,
            padding: "7px 9px",
            borderRadius: 12,
            background: "#f8fbff",
            border: "1px solid #dbe7ff",
            textAlign: "center",
            display: isCompact ? "none" : "block",
          }}
        >
          <div style={{ fontSize: 11, color: "#5d749c", fontWeight: 700 }}>Overall</div>
          <div style={{ marginTop: 3, fontSize: 15, color: "#0f2f6d", fontWeight: 800 }}>{airport.status.overall}</div>
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr) minmax(180px, 260px)",
          gap: 10,
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>From postcode</span>
          <input
            value={form.postcode}
            onChange={(e) => onChange(airport.key, "postcode", e.target.value)}
            placeholder="e.g. LE1 1GB"
            style={compactInputStyle}
          />
        </label>
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>Flight number (optional)</span>
          <input
            value={form.flight}
            onChange={(e) => onChange(airport.key, "flight", e.target.value.toUpperCase())}
            placeholder={airport.key === "heathrow" ? "e.g. BA123" : "e.g. FR669"}
            style={compactInputStyle}
          />
        </label>
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#38507b" }}>Open planner</span>
          <button
            type="button"
            onClick={() => onSubmit(airport.key)}
            style={{
              ...compactButtonStyle,
              background: airport.accent,
              boxShadow: `0 12px 24px ${airport.accent}33`,
            }}
          >
            Open {airport.code} planner
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          fontSize: 11,
          color: "#567097",
          fontWeight: 600,
        }}
      >
        <span>Live road and rail checks</span>
        <span>Flight-aware timing</span>
        <span>No sign-up required to start</span>
      </div>
    </section>
  );
}

function AirportStatusCard({ airport, onOpen }) {
  const isCompact = useCompactMobile();
  const statusRows = [
    { key: "road", label: "Roads", value: airport.status.road },
    { key: "rail", label: "Rail", value: airport.status.rail },
    { key: "flights", label: "Flights", value: airport.status.flights },
    { key: "terminals", label: "Terminals", value: airport.status.terminals },
    { key: "queues", label: "Queues", value: airport.status.queues },
    { key: "weather", label: "Weather", value: airport.status.weather },
  ];
  const overallText = readableStatusText(airport, "overall", airport.statusReasons?.overall || airport.liveReason || "");

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(airport.key)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onOpen?.(airport.key);
      }}
      style={{
        background: "rgba(255,255,255,0.94)",
        border: "1px solid rgba(191, 210, 239, 0.95)",
        borderRadius: 22,
        padding: 0,
        overflow: "hidden",
        boxShadow: "0 20px 42px rgba(15, 23, 42, 0.12)",
        cursor: "pointer",
        backdropFilter: "blur(16px)",
      }}
      title={overallText || "Open live status preview"}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          padding: isCompact ? "11px 12px" : "12px 16px",
          background:
            airport.code === "LHR"
              ? "linear-gradient(90deg, #061b46 0%, #0d2f73 100%)"
              : "linear-gradient(90deg, #0b4bd1 0%, #1d63f2 100%)",
          color: "#eff6ff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>✈️</span>
          <div style={{ fontSize: isCompact ? 15 : 16, fontWeight: 900, lineHeight: 1.15 }}>{airport.name} ({airport.code})</div>
        </div>
        <div style={{ flex: "0 0 auto", padding: isCompact ? "4px 8px" : "4px 10px", borderRadius: 999, background: "rgba(34,197,94,0.16)", color: "#c7f9d4", fontSize: 10, fontWeight: 900 }}>
          ● LIVE
        </div>
      </div>
      <div style={{ display: "grid", padding: isCompact ? "8px 12px 12px" : "10px 16px 14px" }}>
        {statusRows.map((row) => {
          const tone = statusTone(row.value);
          const readableText = readableStatusHeadline(airport, row.key) || row.value;
          return (
            <div
              key={`${airport.key}-${row.label}`}
              title={readableText}
              style={{
                display: "grid",
                gridTemplateColumns: isCompact ? "minmax(0, 1fr) auto" : "78px minmax(0, 1fr) auto",
                gap: isCompact ? "5px 8px" : 10,
                alignItems: "center",
                padding: "8px 0",
                borderTop: row.key === "road" ? "none" : "1px solid rgba(203, 213, 225, 0.6)",
              }}
            >
              <span style={{ color: "#243b63", fontWeight: 800, fontSize: 13 }}>
                <span style={{ marginRight: 8 }}>{sourceIcons[row.key]}</span>
                {row.label}
              </span>
              <span style={{ color: "#1d355d", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: isCompact ? "clip" : "ellipsis", whiteSpace: isCompact ? "normal" : "nowrap", gridColumn: isCompact ? "1 / -1" : "auto", gridRow: isCompact ? 2 : "auto", lineHeight: 1.35 }}>
                {readableText}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 82,
                  padding: "4px 9px",
                  borderRadius: 999,
                  background: tone.bg,
                  color: tone.fg,
                  fontSize: 11,
                  fontWeight: 800,
                  gridColumn: isCompact ? 2 : "auto",
                  gridRow: isCompact ? 1 : "auto",
                }}
              >
                {row.value}
              </span>
            </div>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 8,
          padding: isCompact ? "10px 12px 0" : "10px 16px 0",
          paddingTop: 10,
          borderTop: "1px solid #e5eefc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, color: "#243b63", fontWeight: 900 }}>{sourceIcons.overall} Overall</span>
        <span
          style={{
            minWidth: isCompact ? 100 : 130,
            textAlign: "center",
            padding: "7px 12px",
            borderRadius: 999,
            background: statusTone(airport.status.overall).bg,
            color: statusTone(airport.status.overall).fg,
            fontSize: 13,
            fontWeight: 900,
          }}
        >
          {airport.status.overall}
        </span>
      </div>
      <div style={{ marginTop: 10, padding: isCompact ? "0 12px 14px" : "0 16px 14px", fontSize: 12, color: "#587097", fontWeight: 700, lineHeight: 1.4 }}>
        {overallText || "Tap for live status details"}
      </div>
    </article>
  );
}

const asPreviewList = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return Array.isArray(fallback) ? fallback.filter(Boolean) : [];
};

const stripStatusText = (text) =>
  String(text || "")
    .replace(/\s+/g, " ")
    .replace(/\s*•\s*/g, " • ")
    .trim();

const parseSignalText = (text, fallbackStatus = "Checking") => {
  const clean = stripStatusText(text);
  const [titlePart, ...detailParts] = clean.split(" • ");
  const maybeStatus = detailParts[detailParts.length - 1] || "";
  const statusWithDetail = maybeStatus.match(/^([a-z _-]+)\s*\(([^)]+)\)$/i);
  const parsedStatus = statusWithDetail?.[1]?.trim();
  const parsedDetail = statusWithDetail?.[2]?.trim();
  const statusCandidate = parsedStatus || maybeStatus;
  const status = /good|clear|on time|moderate|busy|watch|severe|red|amber|delay|slow|checking/i.test(statusCandidate)
    ? statusCandidate.replace(/ service$/i, "").trim()
    : fallbackStatus;
  const detail = parsedDetail
    || (/ service$/i.test(statusCandidate) ? statusCandidate : "")
    || (detailParts.length > 1
      ? detailParts.slice(0, -1).join(" • ")
      : detailParts[0] && detailParts[0] !== statusCandidate
        ? detailParts[0]
        : "");
  return {
    title: titlePart || clean || "Status checking",
    detail,
    status,
  };
};

const terminalSortValue = (terminal) => {
  const match = String(terminal || "").match(/\d+/);
  return match ? Number(match[0]) : 99;
};

const terminalQueueCards = (airport) => {
  const queues = Array.isArray(airport.preview.terminal_queues)
    ? airport.preview.terminal_queues.filter((item) => item?.terminal)
    : [];
  if (queues.length) {
    return [...queues].sort((a, b) => terminalSortValue(a.terminal) - terminalSortValue(b.terminal));
  }

  const terminalParts = String(airport.preview.terminal || "")
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);
  if (terminalParts.length) return terminalParts;

  return airport.code === "LHR"
    ? ["T2 Checking", "T3 Checking", "T4 Checking", "T5 Checking"]
    : [airport.preview.terminal || "Terminal Checking"];
};

const bestRoadSignals = (airport) =>
  asPreviewList(airport.preview.road, [airport.statusHeadlines?.road || airport.statusReasons?.road])
    .slice(0, airport.code === "LHR" ? 4 : 3)
    .map((item) => parseSignalText(item, airport.status.road));

const bestRailSignals = (airport) => {
  const fallback = airport.code === "LHR"
    ? ["Heathrow T2/T3 • Checking", "Heathrow T4 • Checking", "Heathrow T5 • Checking", "Underground • Checking"]
    : ["East Midlands Parkway • Checking", "Derby • Checking", "Nottingham • Checking"];
  return asPreviewList(airport.preview.rail, fallback)
    .slice(0, airport.code === "LHR" ? 4 : 3)
    .map((item) => parseSignalText(item, airport.status.rail));
};

const flightSignalCards = (airport) => {
  const flightStatus = readableFlightText(airport, airport.statusReasons?.flights || airport.statusHeadlines?.flights || airport.preview.flights?.[0]);
  const cards = [
    {
      title: "Current flight pressure",
      detail: flightStatus,
      status: airport.status.flights,
    },
    {
      title: "Airport flow",
      detail: `${airport.preview.arrivals || "0"} arrivals • ${airport.preview.departures || "0"} departures`,
      status: airport.status.flights,
    },
  ];
  const sample = asPreviewList(airport.preview.flights).find(Boolean);
  if (sample) {
    cards.push({
      title: "Latest visible service",
      detail: stripStatusText(sample),
      status: airport.status.flights,
    });
  }
  return cards;
};

const queueSignals = (airport) =>
  asPreviewList(airport.preview.queues, [airport.statusHeadlines?.queues || airport.statusReasons?.queues])
    .slice(0, 4)
    .map((item) => parseSignalText(item, airport.status.queues));

const terminalPressureCards = (airport) => {
  const structured = Array.isArray(airport.preview.terminal_pressure)
    ? airport.preview.terminal_pressure.filter((item) => item && typeof item === "object")
    : [];
  if (structured.length) return structured;
  const terminalParts = String(airport.preview.terminal || "")
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);
  if (terminalParts.length) return terminalParts;
  return airport.code === "LHR"
    ? ["T2 Checking", "T3 Checking", "T4 Checking", "T5 Checking"]
    : [airport.preview.terminal || "Main Terminal Checking"];
};

function PreviewPanel({ airport, onOpenPlanner }) {
  const isCompact = useCompactMobile();
  const roadCards = bestRoadSignals(airport);
  const railCards = bestRailSignals(airport);
  const flightCards = flightSignalCards(airport);
  const queueSignalCards = queueSignals(airport);
  const terminalPressure = terminalPressureCards(airport);
  const queueTerminals = terminalQueueCards(airport);
  const terminalSummary = terminalPressure.length
    ? terminalPressure
        .map((item) => (typeof item === "string" ? item : `${item.terminal || "Terminal"} ${item.label || item.level || "Checking"}`))
        .join(" • ")
    : readableTerminalText(airport, airport.statusReasons?.terminals);
  const hasQueueTerminalCards = queueTerminals.some((item) => typeof item !== "string");
  const cardStyle = previewCardShell(isCompact);

  return (
    <article id={`${airport.key}-live-preview`} style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: isCompact ? 26 : 36, fontWeight: 950, lineHeight: 1.05 }}>{airport.name} ({airport.code})</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "#4f6790", fontWeight: 800 }}>
            Live intelligence preview • Updates every 1-2 min
          </div>
        </div>
        <div
          style={{
            padding: "8px 12px",
            borderRadius: 999,
            background: "rgba(34, 197, 94, 0.16)",
            color: "#166534",
            fontWeight: 800,
            fontSize: 12,
            textTransform: "uppercase",
          }}
        >
          Live
        </div>
      </div>

      <div
        style={{
          marginTop: isCompact ? 12 : 18,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "0.7fr 0.8fr 1.3fr",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            ...previewCardShell(isCompact),
            minHeight: isCompact ? 74 : 82,
          }}
        >
          <div style={{ width: 42, height: 42, borderRadius: 999, background: "rgba(245,158,11,0.20)", display: "grid", placeItems: "center", fontSize: 22 }}>📊</div>
          <div>
            <div style={{ color: "#5d708f", fontSize: 12, fontWeight: 800 }}>Overall status</div>
            <div style={{ color: statusTone(airport.status.overall).fg, fontSize: isCompact ? 22 : 24, fontWeight: 950 }}>
              {airport.status.overall}
            </div>
          </div>
        </div>
        <div
          style={{
            ...previewCardShell(isCompact),
            minHeight: isCompact ? 74 : 82,
          }}
        >
          <div style={{ color: "#5d708f", fontSize: 12, fontWeight: 800 }}>Weather</div>
          <div style={{ marginTop: 4, fontSize: isCompact ? 18 : 20, fontWeight: 900 }}>{airport.preview.weather}</div>
          <div style={{ marginTop: 4, color: "#5d708f", fontSize: 12 }}>Included in leave-time checks</div>
        </div>
        <button
          type="button"
          onClick={() => onOpenPlanner?.(airport.key)}
          style={{
            border: "1px solid #dbe7ff",
            borderRadius: isCompact ? 14 : 18,
            padding: isCompact ? 12 : 16,
            background: "rgba(255,255,255,0.92)",
            color: "#08204a",
            textAlign: "left",
            cursor: "pointer",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
          title={`Open ${airport.code} planner`}
        >
          <div style={{ color: "#5d708f", fontSize: 12, fontWeight: 800 }}>Next step</div>
          <div style={{ marginTop: 4, fontSize: isCompact ? 15 : 16, fontWeight: 900 }}>
            {airport.nextStep || "Open planner for when-to-leave timing"}
          </div>
          <div style={{ marginTop: 8, color: "#1d64d8", fontSize: 12, fontWeight: 900 }}>
            Open {airport.code} planner →
          </div>
        </button>
      </div>

      <div
        style={{
          marginTop: isCompact ? 12 : 16,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1.05fr) minmax(0, 0.95fr)",
          gap: 14,
          alignItems: "start",
        }}
      >
        <StatusSection
          title="Road corridors"
          subtitle="Major routes and worst visible pressure"
          icon="🚘"
          status={airport.status.road}
          items={roadCards}
          columns={isCompact ? 1 : 2}
        />

        <StatusSection
          title="Flights"
          subtitle="Disruption, flow and latest visible service"
          icon="✈️"
          status={airport.status.flights}
          items={flightCards}
          columns={isCompact ? 1 : 2}
        />

        <StatusSection
          title="Rail and Underground"
          subtitle="Airport stations, lines and route status"
          icon="🚆"
          status={airport.status.rail}
          items={railCards}
          columns={isCompact ? 1 : 2}
        />

        <div
          style={previewSectionShell(airport.status.terminals, isCompact)}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: isCompact ? 18 : 20, fontWeight: 900 }}>Terminal pressure</div>
              <div style={{ marginTop: 3, color: "#5d708f", fontSize: 13 }}>Flight delay and cancellation status by terminal</div>
            </div>
            <StatusPill status={airport.status.terminals} />
          </div>
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
              gap: 9,
            }}
          >
            {terminalPressure.map((item, index) =>
              typeof item === "string" ? (
                <TerminalMiniCard key={`${airport.key}-terminal-pressure-${index}-${item}`} text={item} />
              ) : (
                <TerminalPressureCard key={`${airport.key}-terminal-pressure-${index}-${item.terminal || "terminal"}`} terminal={item} />
              )
            )}
          </div>
          <div style={{ marginTop: 8, color: "#4f6790", fontSize: 11, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {terminalSummary}
          </div>
        </div>

        <div
          style={previewSectionShell(airport.status.queues, isCompact)}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: isCompact ? 18 : 20, fontWeight: 900 }}>Queue snapshot</div>
              <div style={{ marginTop: 3, color: "#5d708f", fontSize: 13 }}>Security and border waits by terminal</div>
            </div>
            <StatusPill status={airport.status.queues} />
          </div>
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
              gap: 9,
            }}
          >
            {hasQueueTerminalCards ? queueTerminals.map((item, index) =>
              typeof item === "string" ? (
                <MiniStatusCard key={`${airport.key}-queue-text-${index}-${item}`} item={parseSignalText(item, airport.status.queues)} fallbackStatus={airport.status.queues} />
              ) : (
                <TerminalQueueCard key={`${airport.key}-queue-terminal-${item.terminal}`} terminal={item} />
              )
            ) : queueSignalCards.slice(0, 4).map((item, index) => (
              <MiniStatusCard key={`${airport.key}-queue-signal-card-${index}`} item={item} fallbackStatus={airport.status.queues} />
            ))}
          </div>
          {queueSignalCards.length ? (
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {queueSignalCards.slice(0, 2).map((item, index) => (
                <InlineSignal key={`${airport.key}-queue-signal-${index}`} item={item} status={airport.status.queues} />
              ))}
            </div>
          ) : null}
        </div>
      </div>

    </article>
  );
}

function StatusSection({ title, subtitle, icon, status, items, columns = 2 }) {
  const isCompact = useCompactMobile();
  return (
    <div
      style={previewSectionShell(status, isCompact)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: isCompact ? 18 : 20, fontWeight: 900 }}>
            <span style={{ width: 30, height: 30, borderRadius: 999, background: "#e8f1ff", display: "grid", placeItems: "center" }}>{icon}</span>
            {title}
          </div>
          <div style={{ marginTop: 3, color: "#5d708f", fontSize: 13 }}>{subtitle}</div>
        </div>
        <StatusPill status={status} />
      </div>
      <div
        style={{
          marginTop: 10,
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: 9,
        }}
      >
        {(items?.length ? items : [{ title: "Status checking", detail: "Live checks updating", status }]).map((item, index) => (
          <MiniStatusCard key={`${title}-${index}-${item.title}`} item={item} fallbackStatus={status} />
        ))}
      </div>
    </div>
  );
}

function MiniStatusCard({ item, fallbackStatus }) {
  const surface = statusSurface(item.status || fallbackStatus);
  return (
    <div
      title={[item.title, item.detail].filter(Boolean).join(" • ")}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: "9px 10px",
        minWidth: 0,
        boxShadow: "inset 3px 0 0 rgba(37, 99, 235, 0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
        <div style={{ minWidth: 0, color: "#0b1f45", fontSize: 13, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.title}
        </div>
        <StatusPill status={item.status || fallbackStatus} compact />
      </div>
      <div style={{ marginTop: 5, color: "#4f6790", fontSize: 11.5, lineHeight: 1.32 }}>
        {item.detail || "Live status available"}
      </div>
    </div>
  );
}

function InlineSignal({ item, status }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 8,
        alignItems: "center",
        borderTop: "1px solid #e6eefb",
        paddingTop: 6,
        fontSize: 11,
      }}
    >
      <span style={{ color: "#4f6790", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.title}{item.detail ? ` • ${item.detail}` : ""}
      </span>
      <StatusPill status={item.status || status} compact />
    </div>
  );
}

function TerminalMiniCard({ text }) {
  const clean = stripStatusText(text);
  const bulletParts = clean.split(" • ").map((part) => part.trim()).filter(Boolean);
  const terminalMatch = clean.match(/^(T\d+)\s+(.+)$/i);
  const label = bulletParts.length > 1
    ? bulletParts[0]
    : terminalMatch?.[1] || clean || "Terminal";
  const status = bulletParts.length > 1
    ? bulletParts.slice(1).join(" • ")
    : terminalMatch?.[2] || "Status checking";
  const surface = statusSurface(status);
  return (
    <div
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {label}
        </span>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

function TerminalPressureCard({ terminal }) {
  const status = terminal.label || terminal.level || "Checking";
  const surface = statusSurface(status);
  const flights = Number(terminal.flights || 0);
  const arrivals = Number(terminal.arrivals || 0);
  const departures = Number(terminal.departures || 0);
  const cancelled = Number(terminal.cancelled || 0);
  const delayed30 = Number(terminal.delayed_30 || 0);
  const delayed60 = Number(terminal.delayed_60 || 0);
  const pressureLine = `${flights} flights • ${arrivals} arr • ${departures} dep`;
  const disruptionLine = `${cancelled} cancel • ${delayed30} delayed 30m+${delayed60 ? ` • ${delayed60} 60m+` : ""}`;
  return (
    <div
      title={`${terminal.terminal || "Terminal"} • ${pressureLine} • ${disruptionLine}`}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
        display: "grid",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {terminal.terminal || "T"}
        </span>
        <StatusPill status={status} />
      </div>
      <div style={{ color: "#274060", fontSize: 10.5, lineHeight: 1.25, fontWeight: 750 }}>{pressureLine}</div>
      <div style={{ color: cancelled || delayed30 ? statusTone(status).fg : "#4f6790", fontSize: 10.5, lineHeight: 1.25, fontWeight: 850 }}>
        {disruptionLine}
      </div>
    </div>
  );
}

function TerminalQueueCard({ terminal }) {
  const lines = Array.isArray(terminal.lines) ? terminal.lines.filter(Boolean).slice(0, 3) : [];
  const surface = statusSurface(terminal.label || terminal.level);
  return (
    <div
      title={lines.map((line) => `${line.label} ${line.wait}`).join(" • ")}
      style={{
        background: surface.bg,
        border: `1px solid ${surface.border}`,
        borderRadius: 10,
        padding: 9,
        display: "grid",
        gap: 7,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ width: 32, height: 32, borderRadius: 999, background: "#dbeafe", color: "#1e3a8a", display: "grid", placeItems: "center", fontWeight: 950 }}>
          {terminal.terminal}
        </span>
        <StatusPill status={terminal.label || "Checking"} />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        {lines.length ? (
          lines.map((line) => (
            <div key={`${terminal.terminal}-${line.label}`} style={{ display: "flex", justifyContent: "space-between", gap: 6, color: "#274060", fontSize: 11, lineHeight: 1.2 }}>
              <span style={{ color: "#4f6790" }}>{line.label}</span>
              <strong style={{ color: statusTone(line.level).fg }}>{line.wait}</strong>
            </div>
          ))
        ) : (
          <div style={{ color: "#4f6790", fontSize: 11 }}>Queues checking</div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 44,
  borderRadius: 12,
  border: "1px solid #cddcf5",
  background: "#f8fbff",
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
  outline: "none",
};

const buttonStyle = {
  width: "100%",
  minHeight: 44,
  borderRadius: 12,
  border: "none",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 800,
  cursor: "pointer",
};

export function AirportStatusOverview({
  title = "Live airport status",
  subtitle = "Live road, rail, flight, terminal, queue and weather checks from Airport Journey Planner.",
  showDetailedPreviews = true,
  maxWidth = 1320,
  hideDetailedOnMobile = false,
}) {
  const navigate = useNavigate();
  const isCompact = useCompactMobile();
  const [liveAirports, setLiveAirports] = useState(airports);
  const airportList = useMemo(() => [liveAirports.heathrow, liveAirports.ema], [liveAirports]);

  useEffect(() => {
    let cancelled = false;

    const loadAirportIntelligence = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR,EMA");
        if (cancelled) return;
        const next = { ...airports };
        for (const airport of res?.data?.airports || []) {
          const key = airportKeyByCode[String(airport?.code || "").toUpperCase()];
          if (!key) continue;
          next[key] = normalizeLiveAirport(airports[key], airport);
        }
        setLiveAirports(next);
      } catch (_err) {
        if (!cancelled) setLiveAirports(airports);
      }
    };

    loadAirportIntelligence();
    const timer = setInterval(loadAirportIntelligence, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const openPreview = (airportKey) => {
    const el = typeof document !== "undefined" ? document.getElementById(`${airportKey}-live-preview`) : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openPlanner = (airportKey) => {
    const airport = airports[airportKey];
    if (airport?.path) navigate(airport.path);
  };

  return (
    <section style={{ padding: isCompact ? "18px 14px 12px" : "26px 16px 16px" }}>
      <div style={{ maxWidth, margin: "0 auto", display: "grid", gap: isCompact ? 14 : 18 }}>
        <div>
          <div style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            Airport Journey Planner live status
          </div>
          <h2 style={{ margin: "8px 0 6px", fontSize: isCompact ? 24 : 32, color: "#12397c", lineHeight: 1.12 }}>
            {title}
          </h2>
          <p style={{ margin: 0, maxWidth: 760, fontSize: isCompact ? 14 : 16, lineHeight: 1.55, color: "#51698f" }}>
            {subtitle}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          {airportList.map((airport) => (
            <AirportStatusCard key={airport.key} airport={airport} onOpen={openPreview} />
          ))}
        </div>

        {showDetailedPreviews && !(hideDetailedOnMobile && isCompact)
          ? airportList.map((airport) => (
              <PreviewPanel key={`${airport.key}-minibusgrab-preview`} airport={airport} onOpenPlanner={openPlanner} />
            ))
          : null}
      </div>
    </section>
  );
}

export default function Homenewajp() {
  const navigate = useNavigate();
  const isCompact = useCompactMobile();
  const [liveAirports, setLiveAirports] = useState(airports);
  const [selectedAirportKey, setSelectedAirportKey] = useState("heathrow");
  const [forms, setForms] = useState({
    heathrow: { postcode: "", flight: "" },
    ema: { postcode: "", flight: "" },
  });

  useEffect(() => {
    let cancelled = false;

    const loadAirportIntelligence = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR,EMA");
        if (cancelled) return;
        const next = { ...airports };
        for (const airport of res?.data?.airports || []) {
          const key = airportKeyByCode[String(airport?.code || "").toUpperCase()];
          if (!key) continue;
          next[key] = normalizeLiveAirport(airports[key], airport);
        }
        setLiveAirports(next);
      } catch (_err) {
        if (!cancelled) setLiveAirports(airports);
      }
    };

    loadAirportIntelligence();
    const timer = setInterval(loadAirportIntelligence, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const airportList = useMemo(() => [liveAirports.heathrow, liveAirports.ema], [liveAirports]);

  const updateField = (airportKey, field, value) => {
    setForms((prev) => ({
      ...prev,
      [airportKey]: {
        ...prev[airportKey],
        [field]: value,
      },
    }));
  };

  const submitPlanner = (airportKey) => {
    const airport = airports[airportKey];
    const form = forms[airportKey];
    const params = new URLSearchParams();
    const postcode = String(form.postcode || "").trim();
    const flight = String(form.flight || "").trim().toUpperCase();

    if (postcode) params.set("pickup_postcode", postcode);
    if (flight) params.set("flight_number", flight);

    navigate(params.toString() ? `${airport.path}?${params.toString()}` : airport.path);
  };

  const openPreview = (airportKey) => {
    const el = typeof document !== "undefined" ? document.getElementById(`${airportKey}-live-preview`) : null;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ background: "linear-gradient(180deg, #f7fbff 0%, #eef5ff 42%, #f7fbff 100%)" }}>
      <Helmet>
        <title>Airport Journey Planner | Live Road, Rail and Flight Intelligence</title>
        <meta
          name="description"
          content="Plan journeys to Heathrow and East Midlands Airport with live road, rail, weather and flight intelligence. Open the airport-specific planner with postcode and optional flight handoff."
        />
        <meta
          name="keywords"
          content="airport journey planner, Heathrow journey planner, East Midlands Airport journey planner, live airport status, flight checker, airport rail status, airport traffic"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.airportjourneyplanner.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Airport Journey Planner" />
        <meta
          property="og:description"
          content="Live airport journey planning for Heathrow and East Midlands Airport, powered by road, rail, weather and flight intelligence."
        />
        <meta property="og:url" content="https://www.airportjourneyplanner.com/" />
        <meta property="og:image" content="https://www.airportjourneyplanner.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Airport Journey Planner" />
        <meta
          name="twitter:description"
          content="Live road, rail, flight, weather and terminal intelligence for UK airport journeys."
        />
        <meta name="twitter:image" content="https://www.airportjourneyplanner.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://www.airportjourneyplanner.com/#website",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                description:
                  "Live UK airport journey planning with flight, terminal, rail, road, weather and queue intelligence.",
                inLanguage: "en-GB",
              },
              {
                "@type": "Organization",
                "@id": "https://www.airportjourneyplanner.com/#organization",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                logo: "https://www.airportjourneyplanner.com/logo.png",
                parentOrganization: {
                  "@type": "Organization",
                  name: "CheapCabGrab",
                  url: "https://www.cheapcabgrab.com/",
                },
              },
              {
                "@type": ["SoftwareApplication", "WebApplication"],
                "@id": "https://www.airportjourneyplanner.com/#app",
                name: "Airport Journey Planner",
                url: "https://www.airportjourneyplanner.com/",
                applicationCategory: "TravelApplication",
                operatingSystem: "All",
                description:
                  "A live airport journey planning tool for Heathrow and East Midlands Airport.",
                offers: {
                  "@type": "Offer",
                  price: "0",
                  priceCurrency: "GBP",
                },
                featureList: [
                  "Live airport status summaries",
                  "Flight-aware journey planning",
                  "Road and rail intelligence",
                  "Weather and terminal queue signals",
                ],
              },
              {
                "@type": "ItemList",
                "@id": "https://www.airportjourneyplanner.com/#supported-airports",
                name: "Supported airports",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Heathrow Airport",
                    url: "https://www.airportjourneyplanner.com/heathrow",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "East Midlands Airport",
                    url: "https://www.airportjourneyplanner.com/east-midlands-airport",
                  },
                ],
              },
            ],
          })}
        </script>
      </Helmet>
      <section
        style={{
          padding: isCompact ? "18px 16px 18px" : "42px 16px 28px",
          background:
            "linear-gradient(90deg, rgba(239,245,255,0.96) 0%, rgba(215,229,248,0.84) 40%, rgba(184,208,236,0.78) 100%), url('/ajp-bg.png') center/cover no-repeat",
          borderBottom: "1px solid #dbe7ff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: "-4%",
            bottom: 0,
            width: isCompact ? "100%" : "58%",
            height: isCompact ? "40%" : "82%",
            background:
              "linear-gradient(90deg, rgba(241,247,255,0.9) 0%, rgba(233,242,253,0.44) 34%, rgba(233,242,253,0.08) 100%), radial-gradient(circle at 35% 35%, rgba(37,99,235,0.14), rgba(37,99,235,0) 42%), linear-gradient(135deg, rgba(120,170,210,0.24), rgba(10,49,102,0.06))",
            clipPath: "polygon(20% 10%, 100% 0, 100% 100%, 0 100%)",
            opacity: isCompact ? 0.26 : 0.9,
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 1320, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 0.92fr) minmax(620px, 1.08fr)",
              gap: isCompact ? 16 : 32,
              alignItems: "start",
            }}
          >
            <div
              style={{
                position: "relative",
                minHeight: isCompact ? "auto" : 410,
                padding: isCompact ? "4px 0 0" : "42px 0 0",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 13px",
                  borderRadius: 999,
                  background: "#dbeafe",
                  color: "#1d4ed8",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                <span style={{ color: "#16a34a" }}>●</span> Live airport journey planning
              </div>
              <h1
                style={{
                  margin: isCompact ? "14px 0 10px" : "28px 0 18px",
                  fontSize: isCompact ? 42 : 78,
                  lineHeight: isCompact ? 1.03 : 0.98,
                  color: "#071948",
                  letterSpacing: 0,
                  maxWidth: isCompact ? "100%" : 760,
                  fontWeight: 950,
                }}
              >
                Plan your airport journey with <span style={{ color: "#075bea" }}>live</span> intelligence
              </h1>
              <p
                style={{
                  margin: 0,
                  maxWidth: isCompact ? "100%" : 690,
                  fontSize: isCompact ? 15 : 20,
                  lineHeight: 1.55,
                  color: "#5a6b87",
                }}
              >
                Real-time data from flights, roads, rail, terminals, queues, weather and more to help you leave on time, every time.
              </p>

              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: isCompact ? 8 : 10,
                }}
              >
                {heroChips.map((chip) => (
                  <span
                    key={chip}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      padding: isCompact ? "7px 9px" : "10px 14px",
                      borderRadius: 9,
                      background: "rgba(255,255,255,0.72)",
                      border: "1px solid #dbe7ff",
                      color: "#214c91",
                      fontWeight: 700,
                      fontSize: isCompact ? 11 : 12,
                      boxShadow: "0 8px 18px rgba(42, 82, 140, 0.05)",
                    }}
                  >
                    <span style={{ color: "#2563eb" }}>⌁</span>
                    {chip}
                  </span>
                ))}
              </div>

            </div>

            <div>
              <div
                style={{
                  display: "grid",
                  gap: 10,
                  background: "transparent",
                  border: "none",
                  borderRadius: 20,
                  padding: 0,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 12,
                    color: "#071948",
                    fontWeight: 900,
                    fontSize: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                    <span>Live airport status</span>
                    <span style={{ color: "#16a34a", fontSize: 13 }}>●</span>
                    <span style={{ color: "#71829b", fontSize: 14, fontWeight: 700 }}>Updates every 1-2 min</span>
                  </div>
                  <a
                    href="/analytics-v2"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate("/analytics-v2");
                    }}
                    style={{
                      marginLeft: "auto",
                      color: "#245aa9",
                      fontWeight: 800,
                      cursor: "pointer",
                      padding: "2px 0",
                      fontSize: isCompact ? 13 : 14,
                      display: "inline-flex",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isCompact ? "View all →" : "View full analytics →"}
                  </a>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompact ? "1fr" : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {airportList.map((airport) => (
                    <AirportStatusCard key={airport.key} airport={airport} onOpen={openPreview} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <HeroPlannerCard
            airportKey={selectedAirportKey}
            airportList={airportList}
            form={forms[selectedAirportKey]}
            onAirportChange={setSelectedAirportKey}
            onChange={(field, value) => updateField(selectedAirportKey, field, value)}
            onSubmit={submitPlanner}
          />

        </div>
      </section>

      <section style={{ padding: isCompact ? "14px 14px 8px" : "20px 16px 10px" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto", display: "grid", gap: isCompact ? 14 : 18 }}>
          {airportList.map((airport) => (
            <PreviewPanel key={`${airport.key}-detail-preview`} airport={airport} onOpenPlanner={submitPlanner} />
          ))}
        </div>
      </section>

      <section style={{ padding: isCompact ? "10px 14px 6px" : "14px 16px 10px" }}>
        <div
          style={{
            maxWidth: 1360,
            margin: "0 auto",
            background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,251,255,0.9) 100%)",
            border: "1px solid rgba(206,220,243,0.95)",
            borderRadius: isCompact ? 18 : 28,
            boxShadow: "0 20px 48px rgba(15,23,42,0.1)",
            backdropFilter: "blur(14px)",
            display: "grid",
            gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))",
            gap: 0,
            overflow: "hidden",
          }}
        >
          {heroMetrics.map((item, index) => (
            <div
              key={item.label}
              style={{
                padding: isCompact ? "16px 14px" : "24px 28px",
                borderLeft: !isCompact && index > 0 ? "1px solid rgba(213,224,244,0.95)" : "none",
                borderTop: isCompact && index >= 2 ? "1px solid rgba(213,224,244,0.95)" : "none",
                display: "grid",
                gap: 8,
                justifyItems: isCompact ? "start" : "center",
                alignContent: "center",
                minHeight: isCompact ? 104 : 132,
              }}
            >
              <div style={{ color: "#2563eb", fontSize: isCompact ? 18 : 28, fontWeight: 900, lineHeight: 1 }}>{item.icon}</div>
              <div style={{ color: "#0c2f72", fontSize: isCompact ? 20 : 26, fontWeight: 950, lineHeight: 1.02, letterSpacing: 0 }}>{item.value}</div>
              <div style={{ color: "#5c6f8d", fontSize: isCompact ? 12 : 13, fontWeight: 700, lineHeight: 1.25 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: isCompact ? "6px 14px 10px" : "0 16px 14px" }}>
        <div
          style={{
            maxWidth: 1360,
            margin: "0 auto",
            background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(251,253,255,0.93) 100%)",
            border: "1px solid rgba(219,231,255,0.98)",
            borderRadius: isCompact ? 16 : 22,
            padding: isCompact ? "14px 14px" : "18px 22px",
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "auto 1fr",
            alignItems: "center",
            gap: isCompact ? 14 : 24,
            boxShadow: "0 14px 30px rgba(15,23,42,0.06)",
          }}
        >
          <div style={{ color: "#1e3a6d", fontSize: isCompact ? 14 : 16, fontWeight: 900, whiteSpace: isCompact ? "normal" : "nowrap" }}>
            Trusted data from leading sources
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(6, minmax(0, 1fr))",
              gap: isCompact ? 10 : 12,
            }}
          >
            {sourceList.map((item, index) => (
              <div
                key={item.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isCompact ? "flex-start" : "center",
                  gap: 8,
                  minHeight: isCompact ? 38 : 46,
                  padding: isCompact ? "6px 2px" : "6px 8px",
                  borderLeft: !isCompact && index > 0 ? "1px solid rgba(226,234,247,0.9)" : "none",
                  color: "#314363",
                  fontSize: isCompact ? 12 : 14,
                  fontWeight: 800,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: isCompact ? 22 : 26,
                    height: isCompact ? 22 : 26,
                    borderRadius: 999,
                    background: "linear-gradient(180deg, #f4f8ff 0%, #e8f0ff 100%)",
                    color: "#245df0",
                    fontSize: isCompact ? 11 : 13,
                    fontWeight: 900,
                    boxShadow: "inset 0 0 0 1px rgba(183,204,241,0.8)",
                    flex: "0 0 auto",
                  }}
                >
                  {item.mark}
                </span>
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ padding: isCompact ? "14px 14px 6px" : "18px 16px 8px" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto" }}>
          <div style={{ marginBottom: isCompact ? 10 : 16 }}>
            <h2 style={{ margin: 0, fontSize: isCompact ? 24 : 30, color: "#12397c" }}>What the planner checks</h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isCompact ? 10 : 14,
            }}
          >
            {checkCards.map((card) => (
              <article
                key={card.title}
                style={{
                  background: "#ffffff",
                  border: "1px solid #dbe7ff",
                  borderRadius: isCompact ? 12 : 18,
                  padding: isCompact ? 12 : 18,
                  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
                }}
              >
                <div style={{ fontSize: isCompact ? 20 : 28 }}>{card.icon}</div>
                <h3 style={{ margin: isCompact ? "8px 0 6px" : "12px 0 8px", fontSize: isCompact ? 14 : 18, color: "#12397c" }}>{card.title}</h3>
                <p style={{ margin: 0, fontSize: isCompact ? 12 : 14, lineHeight: 1.5, color: "#4b628d" }}>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: isCompact ? "16px 14px 28px" : "22px 16px 38px" }}>
        <div
          style={{
            maxWidth: 1320,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "minmax(0, 1fr) minmax(300px, 0.9fr)",
            gap: isCompact ? 12 : 18,
          }}
        >
          <article
            style={{
              background: "#ffffff",
              border: "1px solid #dbe7ff",
              borderRadius: isCompact ? 14 : 22,
              padding: isCompact ? 16 : 22,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
            }}
          >
            <h2 style={{ margin: 0, fontSize: isCompact ? 22 : 28, color: "#12397c" }}>How it helps</h2>
            <div style={{ marginTop: isCompact ? 12 : 18, display: "grid", gap: isCompact ? 12 : 16 }}>
              {helpSteps.map((step, idx) => (
                <div key={step.title} style={{ display: "grid", gridTemplateColumns: isCompact ? "28px minmax(0, 1fr)" : "36px minmax(0, 1fr)", gap: isCompact ? 10 : 14, alignItems: "start" }}>
                  <div
                    style={{
                      width: isCompact ? 28 : 36,
                      height: isCompact ? 28 : 36,
                      borderRadius: 999,
                      background: "#dbeafe",
                      color: "#1d4ed8",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 900,
                      fontSize: isCompact ? 12 : 16,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: isCompact ? 14 : 18, fontWeight: 800, color: "#12397c" }}>{step.title}</div>
                    <div style={{ marginTop: 5, fontSize: isCompact ? 12 : 15, lineHeight: 1.55, color: "#51698f" }}>{step.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article
            style={{
              background: "#ffffff",
              border: "1px solid #dbe7ff",
              borderRadius: isCompact ? 14 : 22,
              padding: isCompact ? 16 : 22,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
            }}
          >
            <h2 style={{ margin: 0, fontSize: isCompact ? 22 : 28, color: "#12397c" }}>Coverage</h2>
            <div style={{ marginTop: 16, fontSize: 13, color: "#0f766e", fontWeight: 800 }}>Live now</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {coverageLive.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "#ecfdf5",
                    border: "1px solid #bbf7d0",
                    color: "#166534",
                    fontSize: 13,
                    fontWeight: 800,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 18, fontSize: 13, color: "#7c3aed", fontWeight: 800 }}>Coming soon</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {coverageSoon.map((item) => (
                <span
                  key={item}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #dbe2ea",
                    color: "#475569",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
            <p style={{ margin: "18px 0 0", fontSize: 14, lineHeight: 1.6, color: "#51698f" }}>
              More airports and deeper live modules can be added gradually without changing the overall homepage layout.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
}
